import React, { useState, useEffect } from "react";
import * as yup from "yup";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useGetCommonBackgroundQuery } from "../features/api/publicApiSlice";
import FormSelect from "../components/form/FormSelect";

import {
  useTheme,
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  FormControl,
  Container,
  Grid,
  Divider,
  List,
  ListItem,
} from "@mui/material";
import { Close, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import FormTextField from "../components/form/FormTextField";
import FormPhoneNumber from "../components/form/FormPhoneNumber";
import { mobileNumberSchema } from "../utils/phoneValidation";
import FormDatePicker from "../components/form/FormDatePicker";
import Image from "../components/common/Image";
import {
  useLazyGetOtpQuery,
  useRegisterAccountMutation,
  useGetPublicBankAccountsQuery,
  useRegisterAccountWithBankMutation
} from "../features/api/publicApiSlice";
import { setAlert } from "../features/slices/alertSlice";
import { setCredentials } from "../features/slices/authSlice";
import { selectCurrentToken } from "../features/slices/authSlice";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import ImageCarousel from "../components/common/ImageCarousel";
import * as Constant from "../features/constant";
import Cookies from "js-cookie";
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import {
  useAddFingerprintMutation,
} from "../features/api/publicApiSlice";

// pending adding request mobile otp logic
const Register = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletView = useMediaQuery(theme.breakpoints.down("md"));
  const [detailValue, setDetailValue] = useState({ onThisPage: true });
  const [profileValue, setProfileValue] = useState({});
  const token = useSelector(selectCurrentToken);
  const lang = localStorage.getItem("language");
  const [searchParams, setSearchParams] = useSearchParams();

  // const bonusSliderImage_en = [
  //   process.env.REACT_APP_BONUS_SLIDER + "welcome.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "unlimited_deposit.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "slot_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "live_and_sport_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "referral.jpg",
  // ];

  // const bonusSliderImage_bd = [
  //   process.env.REACT_APP_BONUS_SLIDER + "welcome.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "unlimited_deposit.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "slot_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "live_and_sport_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "referral.jpg",
  // ];

  const BONUS_SLIDER_BASE = process.env.REACT_APP_BONUS_SLIDER || "";

  const isOtpInDetail = process.env.REACT_APP_REGISTER_OTP_IN_DETAIL === "true";

  function getBonusSliderImages(envVar) {
    if (!envVar) return [];
    return envVar.split(",").map(function (fileName) {
      return BONUS_SLIDER_BASE + fileName.trim();
    });
  }

  const bonusSliderImage_en = getBonusSliderImages(process.env.REACT_APP_BONUS_SLIDER_IMAGES_EN);
  const bonusSliderImage_bd = getBonusSliderImages(process.env.REACT_APP_BONUS_SLIDER_IMAGES_BD);

  const isEmailEnabled = process.env.REACT_APP_EMAIL_REGISTER !== "false";

  const {
    data: commonBackground,
    isLoading: isCommonBackgroundLoading,
    isSuccess: isCommonBackgroundSuccess,
  } = useGetCommonBackgroundQuery({ prefetch: true });

  const {
    data: bankDetails,
    isLoading: isBankDetailsLoading,
    isError: isBankDetailsError,
    isSuccess: isBankDetailsSuccess,
    error: bankDetailsError,
  } = useGetPublicBankAccountsQuery();

  const registerDetailSchema = yup.object().shape({
    username: yup
      .string()
      .required(t("validation.username"))
      .test(
        "len",
        "Username must be between 8 to 20 characters.",
        (val) => val?.length >= 8 && val?.length <= 20
      ),
    password: yup
      .string()
      .required(t("validation.password"))
      .test(
        "len",
        t("validation.passwordlength"),
        (val) => val?.length >= 8 && val?.length <= 20
      ),
    confirmPassword: yup
      .string()
      .required(t("validation.confirmpassword"))
      .oneOf([yup.ref("password"), null], t("validation.passwordnotmatch")),

    // Include mobile + otp when enabled
    ...(isOtpInDetail && {
      mobileNumber: mobileNumberSchema(t),
      otp: yup.string().when("showOtp", {
        is: () => commonBackground?.result?.otpFlag === "Y",
        then: yup.string().required(t("validation.otp")),
        otherwise: yup.string(),
      }),
    }),
  });

  const registerProfileSchema = yup.object().shape({
    fullname: yup.string().required(t("validation.fullname")),

    ...(isEmailEnabled && {
      email: yup
        .string()
        .email(t("validation.invalidemail"))
        .required(t("validation.emailisrequired")),
    }),

    ...(!isOtpInDetail && {
      mobileNumber: mobileNumberSchema(t),

      otp: yup.string().when("showOtp", {
        is: () => commonBackground?.result?.otpFlag === "Y",
        then: yup.string().required(t("validation.otp")),
        otherwise: yup.string(),
      }),
    }),

    ...(process.env.REACT_APP_BANK_REGISTER === "true" && {
      bankcode: yup.string().required(t("validation.bankname")),
      bankaccountnumber: yup
        .string()
        .required(t("validation.bankaccno"))
        .matches(/^[0-9]+$/, t("validation.bankaccnoonlynumbers")),
    }),
  });

  const getFingerprint = async () => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();

    return result;
  };

  const [register, { data, isLoading, isError, isSuccess, error, reset }] =
    useRegisterAccountMutation();

  const [addFingerprint, { fingerprintData }] =
    useAddFingerprintMutation();


  const [registerBank, {
    data: dataBank,
    isLoading: isLoadingBank,
    isError: isErrorBank,
    isSuccess: isSuccessBank,
    error: errorBank,
    reset: resetBank
  }] = useRegisterAccountWithBankMutation();

  const [
    getOtp,
    {
      data: otp,
      isFetching: isOtpFetching,
      isLoading: isOtpLoading,
      isError: isOtpError,
      isSuccess: isOtpSuccess,
      error: otpError,
    },
  ] = useLazyGetOtpQuery();

  const {
    control: detailControl,
    handleSubmit: handleSubmitDetail,
    getValues: getDetailValues,
  } = useForm({
    resolver: yupResolver(registerDetailSchema),
  });

  const {
    control: profileControl,
    handleSubmit: handleSubmitProfile,
    getValues,
  } = useForm({
    resolver: yupResolver(registerProfileSchema),
  });

  const submitDetail = async (data) => {
    setDetailValue({ onThisPage: false, ...data });
  };

  useEffect(() => {
    const clickid = searchParams.get("clickid");
    if (clickid) {
      Cookies.set("clickid", clickid, { expires: 7, path: "/" });
    }
  }, []);

  const submitProfile = async (data) => {
    const fbc = Cookies.get("_fbc");
    const fbp = Cookies.get("_fbp");
    const clickId = Cookies.get("clickid") || Cookies.get("rtkclickid-store");
    const ttclid = Cookies.get("ttclid");

    setProfileValue({ ...data });

    // Prefer profile form data, fallback to detail form values
    const finalMobileNumber =
      data?.mobileNumber?.replace(/[^A-Z0-9]+/gi, "") ||
      detailValue?.mobileNumber?.replace(/[^A-Z0-9]+/gi, "");
    const finalOtp = data?.otp || detailValue?.otp;

    // Prepare shared fields
    const sharedData = {
      username: detailValue?.username,
      password: detailValue?.password,
      dateOfBirth: data?.date,
      mobileNumber: finalMobileNumber,
      referralCode: data?.referralCode,
      otp: finalOtp,
      fbc,
      fbp,
      clickId,
      ttclId: ttclid
    };

    if (process.env.REACT_APP_BANK_REGISTER) {
      const formData = {
        ...data,
        ...sharedData,
        bankName: data?.bankcode,
        bankAccountNumber: data?.bankaccountnumber,
        ...(process.env.REACT_APP_COUNTRY === "KR" && {
          idNo: data?.idNo,
          telCo: data?.telCo,
        }),
      };

      await registerBank(formData).unwrap();
    } else {
      const formData = {
        ...data,
        ...sharedData,
      };

      await register(formData).unwrap();
    }

    localStorage.clear();
  };

  useEffect(() => {
    if (!isLoading && isSuccess) {
      (async () => {
        window.registerListener = window.registerListener || window;
        dispatch(
          setAlert({
            isOpen: true,
            message: t("header.registersuccessmessage"),
            variant: "success",
          })
        );

        dispatch(setCredentials({ ...data?.result }));

        const fingerprint = await getFingerprint();

        await addFingerprint({
          fingerprint: fingerprint.visitorId,
          userAgent: navigator.userAgent,
        });

        navigate(Constant.getPath("/"));
        reset();

        // window.fbq('track', 'CompleteRegistration');
        // window.bge('event', 'ec_register', {configId: '904500226561349376'});

        const registerCompleteMessage = { event: 'af_complete_registration' };
        window.registerListener.postMessage(JSON.stringify(registerCompleteMessage));
      })();
    }
  }, [isLoading, isSuccess]);

  useEffect(() => {
    if (!isLoadingBank && isSuccessBank) {
      window.registerListener = window.registerListener || window;
      dispatch(
        setAlert({
          isOpen: true,
          message: t("header.registersuccessmessage"),
          variant: "success",
        })
      );

      dispatch(setCredentials({ ...dataBank?.result }));
      navigate(Constant.getPath("/"));
      resetBank();

      // window.fbq('track', 'CompleteRegistration');
      // window.bge('event', 'ec_register', {configId: '904500226561349376'});

      const registerCompleteMessage = { event: 'af_complete_registration' };
      window.registerListener.postMessage(JSON.stringify(registerCompleteMessage));
    }
  }, [isLoadingBank, isSuccessBank])


  useEffect(() => {
    if (!isLoading && isError) {
      dispatch(
        setAlert({
          isOpen: true,
          message: error?.data?.message,
          variant: "error",
        })
      );
      reset();
    }
  }, [isLoading, isError])

  useEffect(() => {
    if (!isLoadingBank && isErrorBank) {
      dispatch(
        setAlert({
          isOpen: true,
          message: errorBank?.data?.message,
          variant: "error",
        })
      );
      resetBank();
    }
  }, [isLoadingBank, isErrorBank])

  useEffect(() => {
    if (!isOtpLoading && isOtpSuccess) {
      dispatch(
        setAlert({
          isOpen: true,
          message: "Otp is sent",
          variant: "success",
        })
      );
    }

    if (!isOtpLoading && isOtpError) {
      dispatch(
        setAlert({
          isOpen: true,
          message: otpError?.data?.message,
          variant: "error",
        })
      );
    }
  }, [isOtpLoading, isOtpSuccess, isOtpError]);

  const renderDetailSection = () => (
    <form onSubmit={handleSubmitDetail(submitDetail)}>
      <FormTextField
        label={t("mobileloginregister.username")}
        control={detailControl}
        defaultValue={detailValue?.username}
        controllerName="username"
        size="small"
      />
      <FormTextField
        label={t("mobileloginregister.password")}
        control={detailControl}
        defaultValue={detailValue?.password}
        controllerName="password"
        type="password"
        showPasswordToggle
        size="small"
      />
      <FormTextField
        label={t("mobileloginregister.reenterpassword")}
        control={detailControl}
        value={detailValue?.confirmPassword}
        controllerName="confirmPassword"
        type="password"
        showPasswordToggle
        size="small"
      />
      {isOtpInDetail && (
        <>
          <Box sx={{ position: "relative" }}>
            <FormPhoneNumber
              label={t("mobileloginregister.mobilenumber")}
              defaultValue={profileValue?.phoneNumber}
              control={detailControl}
              controllerName="mobileNumber"
              size="small"
            />

            {commonBackground?.result?.otpFlag === "Y" && (
              <>
                <Box alignSelf="center">
                  <Button
                    sx={{
                      position: "absolute",
                      right: 0,
                      marginTop: "17px",
                      marginRight: "1px",
                      zIndex: 10,
                      paddingLeft: "10px",
                      paddingRight: "10px",
                    }}
                    variant="contained"
                    onClick={() => {
                      const mobileNumber = getDetailValues("mobileNumber")?.replace(/[^A-Z0-9]+/gi, "");
                      if (mobileNumber) {
                        getOtp(mobileNumber);
                      }
                    }}
                  >
                    {t("mobileloginregister.getotp")}
                  </Button>
                </Box>

                <FormTextField
                  label={t("mobileloginregister.otp")}
                  defaultValue={profileValue?.otp || ""}
                  control={detailControl}
                  controllerName="otp"
                  type="text"
                  size="small"
                />
              </>
            )}
          </Box>
        </>
      )}
      <Box display="flex" justifyContent="center" mt="30px">
        <Button variant="contained" type="submit">
          {t("mobileloginregister.next")}
        </Button>
      </Box>
    </form>
  );

  const renderProfileSection = () => (
    <form onSubmit={handleSubmitProfile(submitProfile)}>
      <Box display="flex" flexDirection="row" alignItems="center" mt="10px">
        <IconButton
          edge="start"
          onClick={() => setDetailValue({ onThisPage: true })}
        >
          <ArrowBackIcon sx={{ color: process.env.REACT_APP_COLOR_TEXT || undefined }} />
        </IconButton>
        <Typography py="5px" color={process.env.REACT_APP_COLOR_TEXT || "white"}>
          {" "}
          {t("mobileloginregister.back")}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column">
        <FormControl>
          <FormTextField
            label={t("mobileloginregister.fullname")}
            defaultValue={profileValue?.fullname || ""}
            control={profileControl}
            controllerName="fullname"
            size="small"
            note={t("mobileloginregister.fullnamenote")}
          />
          {process.env.REACT_APP_BANK_REGISTER && (
            <>
              <FormSelect
                label={t("walletcard.bankaccountpage.bankname")}
                control={profileControl}
                controllerName="bankcode"
                items={bankDetails?.result?.bankConfigs
                  .map((item) => item.bankCode)}
                size="small"
              />
              <FormTextField
                label={t("walletcard.withdrawalpage.bankaccountnumber")}
                defaultValue={profileValue?.bankaccountnumber || ""}
                control={profileControl}
                controllerName="bankaccountnumber"
                type="text"
                size="small"
              />
            </>
          )}
          {process.env.REACT_APP_COUNTRY === "KR" && (
            <>
              <FormTextField
                label={t("mobileloginregister.idno")}
                control={profileControl}
                controllerName="idNo"
                type="text"
                size="small"
                note={t("mobileloginregister.idnote")}
              />
            </>
          )}
          {isEmailEnabled && (
            <FormTextField
              label={t("mobileloginregister.email")}
              defaultValue={profileValue?.email || ""}
              control={profileControl}
              controllerName="email"
              type="email"
              size="small"
            />
          )}
          <FormDatePicker
            control={profileControl}
            label={t("mobileloginregister.dob")}
            value="1990-01-01"
            time={false}
            size="small"
          />
          {process.env.REACT_APP_COUNTRY === "KR" && (
            <>
              <FormSelect
                label={t("mobileloginregister.telco")}
                control={profileControl}
                controllerName="telCo"
                items={Object.entries(
                  t("mobileloginregister.telcoOptions", { returnObjects: true }) ?? {}
                ).map(([value, label]) => ({
                  value,
                  label,
                }))}
                size="small"
              />
            </>
          )}
          {!isOtpInDetail && (
            <>
              <FormPhoneNumber
                label={t("mobileloginregister.mobilenumber")}
                defaultValue={profileValue?.phoneNumber}
                control={profileControl}
                controllerName="mobileNumber"
                size="small"
              />

              {commonBackground?.result?.otpFlag === "Y" && (
                <>
                  <Box alignSelf="center">
                    <Button
                      sx={{
                        position: "absolute",
                        right: 0,
                        marginTop: "17px",
                        marginRight: "1px",
                        zIndex: 10,
                        paddingLeft: "10px",
                        paddingRight: "10px",
                      }}
                      variant="contained"
                      onClick={() => {
                        const mobileNumber = getValues().mobileNumber?.replace(
                          /[^A-Z0-9]+/gi,
                          ""
                        );
                        if (mobileNumber) {
                          getOtp(mobileNumber);
                        }
                      }}
                    >
                      {t("mobileloginregister.getotp")}
                    </Button>
                  </Box>

                  <FormTextField
                    label={t("mobileloginregister.otp")}
                    defaultValue={profileValue?.otp || ""}
                    control={profileControl}
                    controllerName="otp"
                    type="text"
                    size="small"
                  />
                </>
              )}
            </>
          )}
          {localStorage.getItem("refCode") ? (
            <FormTextField
              label={t("mobileloginregister.refcode")}
              defaultValue={localStorage.getItem("refCode")}
              control={profileControl}
              controllerName="referralCode"
              type="text"
              size="small"
              disabled={true}
            />
          ) : null}

        </FormControl>
      </Box>
      <Box display="flex" justifyContent="center" mt="30px" paddingBottom="40px">
        <Button variant="contained" type="submit">
          {t("mobileloginregister.register")}
        </Button>
      </Box>
    </form>
  );

  return (
    <Box backgroundColor={theme.palette.background.secondaryPaper} height={mobileView && "100vh"} >
      {mobileView ? (
        <>
          {/* <Box p={2} backgroundColor="black">
            <Grid container justifyContent="space-between">
              <Image
                width="178px"
                src={process.env.REACT_APP_LOGO}
                height="50px"
                onClick={null}
              />
              <IconButton
                onClick={() => {
                  navigate("/");
                }}
              >
                <Close />
              </IconButton>
            </Grid>
          </Box> */}
          <Container>
            <Box mt={12}>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 3,
                }}
              >
                <Box
                  p={2}
                  flexGrow={1}
                  sx={{ cursor: "pointer", position: "relative" }}
                  onClick={() => navigate(Constant.getPath("/login"))}
                >
                  <Typography
                    fontSize={14}
                    fontWeight={800}
                    align="center"
                    color="white"
                  >
                    {t("mobileloginregister.login")}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box
                  p={2}
                  flexGrow={1}
                  sx={{ cursor: "pointer", position: "relative" }}
                >
                  <Typography
                    color={theme.palette.mobileLoginRegTitle}
                    fontSize={14}
                    fontWeight={800}
                    align="center"
                  >
                    {t("mobileloginregister.signup")}
                  </Typography>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 5,
                      left: "50%",
                      transform: "translateX(-50%)",
                      borderBottom: "2px solid",
                      width: "20%",
                      borderColor: theme.palette.mobileLoginRegTitle,
                    }}
                  />
                </Box>
              </Box>
              <Box>
                <ImageCarousel
                  banners={(lang === 'en' || lang === null) ? bonusSliderImage_en : bonusSliderImage_bd}
                  navigation={false}
                  spaceBetween={0}
                />
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                sx={{ backgroundColor: process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY || theme.palette.background.paper }}
                justifyContent="center"
                pb={3}
                px={5}
              >
                <Box>
                  {detailValue.onThisPage
                    ? renderDetailSection()
                    : renderProfileSection()}
                </Box>
              </Box>
            </Box>
          </Container>
        </>
      ) : (
        <Container>
          <Box px={tabletView ? 0 : 20} py={5}>
            <Typography
              fontWeight={800}
              fontSize={14}
              color={theme.palette.primary.main}
              mb={2}
            >
              {t("mobileloginregister.registration")}
            </Typography>
            <Box
              pr="24px"
              py="24px"
              sx={{ backgroundColor: process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY || theme.palette.background.paper }}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box
                sx={{
                  flexGrow: 1,
                  px: 3,
                }}
              >
                <Box pb={3}>
                  <Image width="250px" src={process.env.REACT_APP_LOGO} />
                </Box>
                {detailValue.onThisPage
                  ? renderDetailSection()
                  : renderProfileSection()}
              </Box>
              <Box>
                <Box p={2} sx={{ boxShadow: 3 }}>
                  <Typography fontWeight={600} color={process.env.REACT_APP_COLOR_TEXT || "white"}>
                    {t("mobileloginregister.memberbenefits.title")}
                  </Typography>
                  <List
                    sx={{
                      pl: 2,
                      listStyleType: "disc",
                      "& .MuiListItem-root": {
                        display: "list-item",
                        color: process.env.REACT_APP_COLOR_TEXT || 'white',
                      },
                    }}
                  >
                    <ListItem>
                      <Typography fontSize={12} color={process.env.REACT_APP_COLOR_TEXT || "white"}>
                        {t("mobileloginregister.memberbenefits.firstpoint")}
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography fontSize={12} color={process.env.REACT_APP_COLOR_TEXT || "white"}>
                        {t("mobileloginregister.memberbenefits.secondpoint")}
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography fontSize={12} color={process.env.REACT_APP_COLOR_TEXT || "white"}>
                        {t("mobileloginregister.memberbenefits.thirdpoint")}
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography fontSize={12} color={process.env.REACT_APP_COLOR_TEXT || "white"}>
                        {t("mobileloginregister.memberbenefits.fourthpoint")}
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography fontSize={12} color={process.env.REACT_APP_COLOR_TEXT || "white"}>
                        {" "}
                        {t("mobileloginregister.memberbenefits.fifthpoint")}
                      </Typography>
                    </ListItem>
                  </List>
                </Box>
                <Box pt={2}>
                  <Image
                    width="350px"
                    src={(lang === 'en' || lang === null) ? process.env.REACT_APP_REG_BANNER_1 : process.env.REACT_APP_REG_BANNER_4}
                  />
                </Box>
                <Box pt={2}>
                  <Image
                    width="350px"
                    src={(lang === 'en' || lang === null) ? process.env.REACT_APP_REG_BANNER_2 : process.env.REACT_APP_REG_BANNER_5}
                  />
                </Box>
                <Box pt={2}>
                  <Image
                    width="350px"
                    src={(lang === 'en' || lang === null) ? process.env.REACT_APP_REG_BANNER_3 : process.env.REACT_APP_REG_BANNER_6}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      )}
    </Box>
  );
};

export default Register;
